import React, { useState, useEffect, useRef } from 'react';
import {
  Row,
  Col,
  Card,
  Badge,
  Form,
  InputGroup,
  Button,
  Collapse,
  Table,
  Spinner,
  ProgressBar,
  OverlayTrigger,
  Tooltip,
  Pagination,
} from 'react-bootstrap';
import {
  Users,
  Search,
  CheckCircle,
  Clock,
  XCircle,
  ChevronDown,
  ChevronUp,
  Zap,
  Star,
} from 'lucide-react';
import AssignTaskModal from './AssignTaskModal';
import TaskFormModal from '../modals/TaskFormModal';
import './StaffMonitoringSection.css';

const ELLIPSIS = '…';

/** Dãy số trang rút gọn: luôn có trang đầu/cuối, 1 trang kề hai bên trang hiện tại. */
const getPageNumbers = (current, total) => {
  const pages = [];
  for (let p = 1; p <= total; p += 1) {
    if (p === 1 || p === total || Math.abs(p - current) <= 1) {
      pages.push(p);
    } else if (pages[pages.length - 1] !== ELLIPSIS) {
      pages.push(ELLIPSIS);
    }
  }
  return pages;
};

const StaffMonitoringSection = ({
  data,
  loading,
  onTaskAssigned,
  search = '',
  onSearchChange,
  status = 'ALL',
  onStatusChange,
  page = 1,
  onPageChange,
}) => {
  // Ô tìm kiếm gõ tại chỗ, chỉ bắn lên server (đổi trang) sau khi ngừng gõ
  const [searchInput, setSearchInput] = useState(search);
  const [expandedEmployees, setExpandedEmployees] = useState(new Set());
  const [assignModalTarget, setAssignModalTarget] = useState(null);
  const [createTaskConfig, setCreateTaskConfig] = useState(null); // { onSaved }
  const pendingExpandRef = useRef(null);

  useEffect(() => {
    setSearchInput(search);
  }, [search]);

  useEffect(() => {
    if (searchInput === search) return;
    const timer = setTimeout(() => onSearchChange?.(searchInput), 400);
    return () => clearTimeout(timer);
  }, [searchInput, search, onSearchChange]);

  // Khi data refresh xong sau khi giao task, tự động expand card đó
  useEffect(() => {
    if (!pendingExpandRef.current || !data?.shifts) return;
    const shiftId = pendingExpandRef.current;
    pendingExpandRef.current = null;
    setExpandedEmployees((prev) => new Set([...prev, shiftId]));
  }, [data]);

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" />
        <p className="mt-2">Đang tải dữ liệu nhân viên...</p>
      </div>
    );
  }

  if (!data || !data.shifts || !Array.isArray(data.shifts)) {
    return (
      <div className="text-center py-5">
        <p>Không có dữ liệu ca làm việc</p>
      </div>
    );
  }

  // Lọc theo tên + trạng thái và phân trang đều do server xử lý
  const { summary, shifts, meta } = data;
  const pageShifts = shifts || [];
  const totalPages = meta?.total_pages || 1;
  const currentPage = meta?.page || page;

  const toggleShift = (shiftId) => {
    const newExpanded = new Set(expandedEmployees);
    if (newExpanded.has(shiftId)) {
      newExpanded.delete(shiftId);
    } else {
      newExpanded.add(shiftId);
    }
    setExpandedEmployees(newExpanded);
  };

  const getWorkStatusConfig = (status) => {
    const configs = {
      WORKING: { variant: 'success', icon: CheckCircle, text: 'Đang làm', color: '#52c41a' },
      COMPLETED: { variant: 'primary', icon: CheckCircle, text: 'Hoàn thành', color: '#1890ff' },
      ABSENT: { variant: 'danger', icon: XCircle, text: 'Vắng mặt', color: '#f5222d' },
      UPCOMING: { variant: 'info', icon: Clock, text: 'Ca sắp tới', color: '#1890ff' },
    };
    return configs[status] || configs.UPCOMING;
  };

  const getTaskStatusIcon = (status) => {
    const icons = {
      COMPLETED: { icon: '✅', color: '#52c41a' },
      IN_PROGRESS: { icon: '🔄', color: '#1890ff' },
      PENDING: { icon: '⏳', color: '#8c8c8c' },
      OVERDUE: { icon: '❌', color: '#f5222d' },
      CANCELLED: { icon: '🚫', color: '#595959' },
    };
    return icons[status] || icons.PENDING;
  };

  const getProgressColor = (percentage) => {
    if (percentage >= 80) return 'success';
    if (percentage >= 50) return 'warning';
    return 'danger';
  };

  const formatDuration = (minutes) => {
    if (!minutes) return 'N/A';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}p`;
  };

  const getShiftTypeIcon = (shiftType) => {
    const icons = {
      MORNING: '🌅',
      AFTERNOON: '🌆',
      EVENING: '🌃',
      NORMAL: '📅',
    };
    return icons[shiftType] || '📅';
  };

  const adHocCount = (tasks) => tasks?.filter((t) => t.assigned_by).length || 0;

  return (
    <div className="staff-monitoring-section">
      {/* Summary Cards */}
      <Row className="mb-4">
        <Col xs={6} md={3}>
          <Card className="summary-card">
            <Card.Body className="text-center">
              <Users size={32} className="mb-2 text-primary" />
              <div className="summary-number">{summary?.total_shifts || 0}</div>
              <div className="summary-label">Tổng ca</div>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={6} md={3}>
          <Card className="summary-card success">
            <Card.Body className="text-center">
              <CheckCircle size={32} className="mb-2" />
              <div className="summary-number">{summary?.working || 0}</div>
              <div className="summary-label">Đang làm</div>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={6} md={3}>
          <Card className="summary-card danger">
            <Card.Body className="text-center">
              <XCircle size={32} className="mb-2" />
              <div className="summary-number">{summary?.absent || 0}</div>
              <div className="summary-label">Vắng mặt</div>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={6} md={3}>
          <Card className="summary-card info">
            <Card.Body className="text-center">
              <Clock size={32} className="mb-2" />
              <div className="summary-number">{summary?.upcoming || 0}</div>
              <div className="summary-label">Ca sắp tới</div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Filters */}
      <Card className="filters-card mb-4">
        <Card.Body>
          <Row>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Tìm kiếm nhân viên</Form.Label>
                <InputGroup>
                  <InputGroup.Text>
                    <Search size={18} />
                  </InputGroup.Text>
                  <Form.Control
                    type="text"
                    placeholder="Nhập tên nhân viên..."
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                  />
                </InputGroup>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Lọc theo trạng thái</Form.Label>
                <Form.Select
                  value={status}
                  onChange={(e) => onStatusChange?.(e.target.value)}
                >
                  <option value="ALL">Tất cả</option>
                  <option value="WORKING">Đang làm</option>
                  <option value="COMPLETED">Hoàn thành</option>
                  <option value="ABSENT">Vắng mặt</option>
                  <option value="UPCOMING">Ca sắp tới</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Employee Cards Grid */}
      <div className="employees-grid">
        {pageShifts.length > 0 ? (
          pageShifts.map((shiftItem) => {
            const statusConfig = getWorkStatusConfig(shiftItem.work_status);
            const StatusIcon = statusConfig.icon;
            const isExpanded = expandedEmployees.has(shiftItem.shift_id);
            const overdueCount = shiftItem.tasks?.filter((t) => t.is_overdue).length || 0;
            const progressColor = getProgressColor(shiftItem.task_summary?.completion_percentage || 0);
            const shift = shiftItem.shift;
            const canAssignTask = ['WORKING', 'UPCOMING'].includes(shiftItem.work_status);
            const adhocTaskCount = adHocCount(shiftItem.tasks);
            return (
              <Card
                key={shiftItem.shift_id}
                className={`employee-card ${shiftItem.work_status.toLowerCase()}`}
              >
                <Card.Body>
                  {/* Employee Header */}
                  <div className="employee-header">
                    <div className="employee-info">
                      <div className="employee-name">
                        {shiftItem.full_name}
                        {adhocTaskCount > 0 && (
                          <OverlayTrigger
                            placement="top"
                            overlay={<Tooltip>{adhocTaskCount} task ad-hoc đang chạy</Tooltip>}
                          >
                            <span className="adhoc-indicator ms-2">
                              <Zap size={12} />
                              {adhocTaskCount}
                            </span>
                          </OverlayTrigger>
                        )}
                      </div>
                      <div className="employee-role">
                        {shiftItem.role} | {shift.branch_name || 'N/A'}
                      </div>
                    </div>
                    <div className="d-flex flex-column align-items-end gap-1">
                      <Badge bg={statusConfig.variant} className="status-badge">
                        <StatusIcon size={14} />
                        <span className="ms-1">{statusConfig.text}</span>
                      </Badge>
                      {canAssignTask && (
                        <Button
                          variant="warning"
                          size="sm"
                          className="assign-task-btn"
                          onClick={() => setAssignModalTarget(shiftItem)}
                        >
                          <Zap size={12} className="me-1" />
                          Giao Task
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* Shift Info */}
                  <div className="shift-info mt-3">
                    <div className="shift-type">
                      {getShiftTypeIcon(shift.shift_type || 'NORMAL')} {shift.shift_type || 'NORMAL'}
                    </div>
                    <div className="shift-time">
                      {new Date(shift.start_time).toLocaleTimeString('vi-VN', {
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                      {' - '}
                      {new Date(shift.end_time).toLocaleTimeString('vi-VN', {
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </div>
                    {shift.is_cashier && (
                      <Badge bg="info" className="mt-1">
                        Thu ngân
                      </Badge>
                    )}
                  </div>

                  {/* Working Duration */}
                  {shiftItem.working_duration_minutes && (
                    <div className="working-duration mt-2">
                      <Clock size={16} />
                      <span>Đã làm: {formatDuration(shiftItem.working_duration_minutes)}</span>
                    </div>
                  )}

                  {/* Attendance Info */}
                  {shiftItem.attendance && (
                    <div className="attendance-info mt-2">
                      {shiftItem.attendance.late_minutes > 0 ? (
                        <Badge bg="warning">⚠️ Trễ {shiftItem.attendance.late_minutes} phút</Badge>
                      ) : (
                        <Badge bg="success">✅ Đúng giờ</Badge>
                      )}
                    </div>
                  )}

                  {/* Task Summary */}
                  <div className="task-summary mt-3">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <span className="task-label">
                        📋 Tasks: {shiftItem.task_summary?.completed}/{shiftItem.task_summary?.total}
                        {adhocTaskCount > 0 && (
                          <span className="ms-2 adhoc-task-count">
                            <Zap size={11} className="me-1" />
                            {adhocTaskCount} ad-hoc
                          </span>
                        )}
                      </span>
                      <span className="task-percentage">
                        {shiftItem.task_summary?.completion_percentage || 0}%
                      </span>
                    </div>
                    <ProgressBar
                      now={shiftItem.task_summary?.completion_percentage || 0}
                      variant={progressColor}
                      className="task-progress"
                    />
                    {overdueCount > 0 && (
                      <Badge bg="danger" className="mt-2">
                        ⏰ {overdueCount} task overdue
                      </Badge>
                    )}
                  </div>

                  {/* View Tasks Button */}
                  <Button
                    variant="outline-primary"
                    size="sm"
                    className="w-100 mt-3"
                    onClick={() => toggleShift(shiftItem.shift_id)}
                  >
                    {isExpanded ? (
                      <>
                        <ChevronUp size={16} /> Thu gọn
                      </>
                    ) : (
                      <>
                        <ChevronDown size={16} /> Xem tasks
                      </>
                    )}
                  </Button>

                  {/* Tasks List (Collapsible) */}
                  <Collapse in={isExpanded}>
                    <div className="tasks-list mt-3">
                      {shiftItem.tasks?.length > 0 ? (
                        <Table size="sm" className="tasks-table">
                          <thead>
                            <tr>
                              <th style={{ width: 32 }}></th>
                              <th>Task</th>
                              <th style={{ width: 52 }}>Due</th>
                            </tr>
                          </thead>
                          <tbody>
                            {shiftItem.tasks.map((task) => {
                              const taskStatus = task.is_overdue ? 'OVERDUE' : task.status;
                              const statusIcon = getTaskStatusIcon(taskStatus);
                              const isAdHoc = !!task.assigned_by;
                              const effectiveBonus =
                                task.bonus_points_percentage ?? task.task?.bonus_points_percentage ?? 0;

                              return (
                                <tr
                                  key={task.id}
                                  className={`${task.is_overdue ? 'overdue' : ''} ${isAdHoc ? 'adhoc-row' : ''}`}
                                >
                                  <td>
                                    <span style={{ color: statusIcon.color }}>{statusIcon.icon}</span>
                                  </td>
                                  <td>
                                    <div className="task-name-cell">
                                      <span className="task-name">{task.name || task.task_name || task.task_type}</span>
                                      <div className="task-badges">
                                        {isAdHoc && (
                                          <span className="badge-adhoc">
                                            <Zap size={9} className="me-1" />
                                            Ad-hoc
                                          </span>
                                        )}
                                        {task.required_for_checkout && (
                                          <span className="badge-required">Bắt buộc</span>
                                        )}
                                        {effectiveBonus > 0 && (
                                          <span className="badge-bonus">
                                            <Star size={9} className="me-1" />
                                            +{effectiveBonus}%
                                          </span>
                                        )}
                                      </div>
                                      {task.assignment_notes && (
                                        <small className="task-notes">{task.assignment_notes}</small>
                                      )}
                                    </div>
                                  </td>
                                  <td>
                                    {task.due_time ? (
                                      <small>
                                        {new Date(task.due_time).toLocaleTimeString('vi-VN', {
                                          hour: '2-digit',
                                          minute: '2-digit',
                                        })}
                                      </small>
                                    ) : (
                                      '-'
                                    )}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </Table>
                      ) : (
                        <p className="text-center text-muted">Không có task</p>
                      )}
                    </div>
                  </Collapse>
                </Card.Body>
              </Card>
            );
          })
        ) : (
          <Card>
            <Card.Body className="text-center py-5">
              <p>Không tìm thấy ca làm việc nào</p>
            </Card.Body>
          </Card>
        )}
      </div>

      {/* Pagination */}
      {meta?.total > 0 && (
        <div className="staff-pagination mt-3 d-flex justify-content-between align-items-center flex-wrap gap-2">
          <small className="text-muted">
            Hiển thị {pageShifts.length} / {meta.total} ca — trang {currentPage}/{totalPages}
          </small>
          {totalPages > 1 && (
            <Pagination className="mb-0">
              <Pagination.Prev
                disabled={currentPage <= 1}
                onClick={() => onPageChange?.(currentPage - 1)}
              />
              {getPageNumbers(currentPage, totalPages).map((p, idx) =>
                p === ELLIPSIS ? (
                  <Pagination.Ellipsis key={`gap-${idx}`} disabled />
                ) : (
                  <Pagination.Item
                    key={p}
                    active={p === currentPage}
                    onClick={() => onPageChange?.(p)}
                  >
                    {p}
                  </Pagination.Item>
                ),
              )}
              <Pagination.Next
                disabled={currentPage >= totalPages}
                onClick={() => onPageChange?.(currentPage + 1)}
              />
            </Pagination>
          )}
        </div>
      )}

      {/* Assign Task Modal */}
      <AssignTaskModal
        show={!!assignModalTarget}
        onHide={() => setAssignModalTarget(null)}
        shiftItem={assignModalTarget}
        onAssigned={(shiftId) => {
          pendingExpandRef.current = shiftId;
          setAssignModalTarget(null);
          onTaskAssigned?.();
        }}
        onCreateTask={(config) => setCreateTaskConfig(config)}
      />

      {/* Create Urgent Task Modal — rendered at this level to avoid stacking conflict */}
      <TaskFormModal
        show={!!createTaskConfig}
        task={{ is_urgent: true, is_repeatable: false }}
        onHide={() => setCreateTaskConfig(null)}
        onSave={(newTask) => {
          createTaskConfig?.onSaved?.(newTask);
          setCreateTaskConfig(null);
        }}
      />
    </div>
  );
};

export default StaffMonitoringSection;
